import firebase from "config/firebase";
import useAuthContext from "contexts/Auth/useAuthContext";
import {signInWithCustomToken} from "firebase/auth";
import {FC, useCallback, useEffect, useRef} from "react";
import {clearVerifier, prepareFirstPKCEStep, retrieveVerifier} from "api/pkce";
import {CAS_FRONTEND_URL} from "api/axios";
import api from "api";

// Capture ?redirect before AuthProvider's mount effect strips it from the URL.
// This runs at module evaluation time (synchronously, before any useEffect).
const INITIAL_REDIRECT = new URLSearchParams(window.location.search).get("redirect") || "/";

/**
 * Client side of the PKCE flow when frontend needs to borrow cas-frontend's session.
 *
 * Flow:
 *   Initiate  — generate verifier, redirect to cas-frontend /auth/oauth
 *   Callback  — exchange ?code for a custom Firebase token, sign in
 */
const SSO: FC = () => {
  const auth = useAuthContext();
  const initiatedRef = useRef(false);

  // Already logged in — navigate to the redirect target captured at module load time.
  // Only fires when there's no ?code (i.e. not a PKCE callback) — in that case
  // AuthProvider's onAuthStateChanged handles the redirect via localStorage.
  const isCallback = !!new URLSearchParams(window.location.search).get("code");
  useEffect(() => {
    if (auth && !isCallback) {
      window.location.href = INITIAL_REDIRECT;
    }
  }, [auth, isCallback]);

  const initiatePKCE = useCallback(async () => {
    const challenge = await prepareFirstPKCEStep();
    // ?redirect was already stripped by AuthProvider and saved to localStorage.
    // Pass the bare callback URI — AuthProvider will handle the final redirect.
    const redirectUri = `${window.location.origin}/auth/sso`;
    const url = new URL(`${CAS_FRONTEND_URL}/auth/oauth`);
    url.searchParams.set("challenge", challenge);
    url.searchParams.set("redirect_uri", redirectUri);
    window.location.href = url.toString();
  }, []);

  const handleCallback = useCallback(
    async (code: string) => {
      const verifier = retrieveVerifier();
      if (!verifier) {
        initiatedRef.current = false;
        await initiatePKCE();
        return;
      }
      const redirectUri = `${window.location.origin}/auth/sso`;
      try {
        const token = await api.exchangeOAuthPKCECode(code, verifier, redirectUri);
        clearVerifier();
        await signInWithCustomToken(firebase.auth, token);
        // AuthProvider's onAuthStateChanged fires and handles the redirect via localStorage.
      } catch {
        initiatedRef.current = false;
        await initiatePKCE();
      }
    },
    [initiatePKCE],
  );

  useEffect(() => {
    // Wait until auth state resolves (undefined = loading).
    if (auth === undefined || auth || initiatedRef.current) return;
    initiatedRef.current = true;

    const sp = new URLSearchParams(window.location.search);
    const code = sp.get("code");

    if (code) {
      handleCallback(code);
    } else {
      initiatePKCE();
    }
  }, [auth, handleCallback, initiatePKCE]);

  return (
    <div className="w-full bg-slate-100 h-full flex items-center justify-center">
      <span className="text-slate-500 text-sm">Authenticating...</span>
    </div>
  );
};

export default SSO;
